import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { X, Search, Filter, Loader2, ArrowRight, Hammer, Wrench, Sparkles, Star, CheckCircle2, Clock, Calendar, Play } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* Project Search — dialog that searches across all areas + their
   enhancements/repairs by name, person responsible, status, and kind.
   Apple-style result cards; clicking one fires onPickArea(area_id) so
   the host page can open AreaDetailDialog. */
export default function ProjectSearchDialog({ open, onClose, onPickArea, areas }) {
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('');
  const [kind, setKind] = useState('');
  const [assignee, setAssignee] = useState('');
  const [results, setResults] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);

  // Build assignee dropdown from areas' responsible_user_emails
  const assigneeOptions = useMemo(() => {
    const set = new Set();
    (areas || []).forEach((a) => (a.responsible_user_emails || []).forEach((e) => set.add(e)));
    return Array.from(set).sort();
  }, [areas]);

  const search = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (q.trim()) params.q = q.trim();
      if (status) params.status = status;
      if (kind) params.kind = kind;
      if (assignee) params.assignee = assignee;
      const { data } = await axios.get(`${API}/projects/search`, { params });
      setResults(data.results || []);
      setCount(data.count || 0);
    } catch (e) {
      setResults([]);
      setCount(0);
    } finally {
      setLoading(false);
    }
  }, [q, status, kind, assignee]);

  // Auto-search on open + every filter change (debounced for q)
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(search, 250);
    return () => clearTimeout(t);
  }, [open, search]);

  if (!open) return null;

  const STATUS_CHIPS = [
    { id: '',           label: 'All statuses' },
    { id: 'completed',  label: 'Completed', icon: CheckCircle2, color: '#15803D' },
    { id: 'in-progress', label: 'In progress', icon: Clock, color: '#B45309' },
    { id: 'planned',    label: 'Planned', icon: Calendar, color: '#1F4FBE' },
  ];
  const KIND_CHIPS = [
    { id: '',                       label: 'All kinds' },
    { id: 'reconstruction',         label: 'Reconstruction', icon: Hammer, color: '#0EA5E9' },
    { id: 'crucial-enhancement',    label: 'Crucial Enhancement', icon: Sparkles, color: '#DC2626' },
    { id: 'requested-enhancement',  label: 'Requested Enhancement', icon: Star, color: '#F59E0B' },
  ];

  return (
    <div data-testid="project-search-dialog" className="fixed inset-0 z-[100] flex items-start justify-center p-4 pt-12" style={{ backgroundColor: 'rgba(15,23,42,0.65)', backdropFilter: 'blur(8px)' }}>
      <div className="w-full max-w-4xl max-h-[88vh] flex flex-col rounded-2xl overflow-hidden shadow-2xl" style={{ backgroundColor: '#FAF8F2' }}>
        {/* Header + search bar */}
        <div className="px-6 py-4 border-b" style={{ backgroundColor: '#0E1F2C', borderColor: '#1F2D3D' }}>
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#A3D4D4' }}>
              <Search className="w-5 h-5" style={{ color: '#0E1F2C' }} />
            </div>
            <div className="flex-1">
              <p className="text-[10px] uppercase tracking-[0.2em] font-bold" style={{ color: '#A3D4D4', fontFamily: 'Manrope' }}>Search</p>
              <h2 className="text-lg font-bold text-white" style={{ fontFamily: 'Manrope' }}>Find a project, area, or enhancement</h2>
            </div>
            <button
              data-testid="project-search-auto-tour"
              onClick={() => {
                onClose();
                setTimeout(() => {
                  try { window.dispatchEvent(new CustomEvent('atrium:start-guided-tour')); } catch (err) { /* no-op */ }
                }, 200);
              }}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-bold text-white transition hover:-translate-y-0.5"
              style={{ background: 'linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)', fontFamily: 'Manrope' }}
              title="Auto-piloted walkthrough — Heavenly narrates the top 8 hotspots"
            >
              <Play className="w-3 h-3" /> Auto-piloted tour
            </button>
            <button data-testid="project-search-close" onClick={onClose} className="p-2 rounded-lg text-white hover:bg-white/10 transition">
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/60" />
            <input
              data-testid="project-search-input"
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Type to search by name, zone, or description…"
              className="w-full pl-10 pr-4 py-3 rounded-xl text-sm border-0 outline-none"
              style={{ backgroundColor: 'rgba(255,255,255,0.10)', color: 'white', fontFamily: 'Manrope' }}
            />
          </div>
        </div>

        {/* Filter chips */}
        <div className="px-6 py-3 border-b flex flex-wrap items-center gap-2" style={{ borderColor: '#E2DBC8', backgroundColor: '#F5F0E1' }}>
          <Filter className="w-3.5 h-3.5" style={{ color: '#5C6B6B' }} />
          {STATUS_CHIPS.map((c) => (
            <FilterChip key={`s-${c.id}`} active={status === c.id} onClick={() => setStatus(c.id)} {...c} testid={`project-search-status-${c.id || 'all'}`} />
          ))}
          <span className="mx-2 text-xs" style={{ color: '#5C6B6B' }}>·</span>
          {KIND_CHIPS.map((c) => (
            <FilterChip key={`k-${c.id}`} active={kind === c.id} onClick={() => setKind(c.id)} {...c} testid={`project-search-kind-${c.id || 'all'}`} />
          ))}
          {assigneeOptions.length > 0 && (
            <>
              <span className="mx-2 text-xs" style={{ color: '#5C6B6B' }}>·</span>
              <select
                data-testid="project-search-assignee"
                value={assignee}
                onChange={(e) => setAssignee(e.target.value)}
                className="px-3 py-1.5 rounded-full text-[11px] font-bold border"
                style={{ borderColor: '#D6D0C0', backgroundColor: 'white', fontFamily: 'Manrope' }}
              >
                <option value="">Anyone responsible</option>
                {assigneeOptions.map((e) => (<option key={e} value={e}>{e}</option>))}
              </select>
            </>
          )}
        </div>

        {/* Results — Apple-style cards */}
        <div className="flex-1 overflow-y-auto p-4">
          <p className="text-[10px] uppercase tracking-[0.15em] font-bold mb-2" style={{ color: '#5C6B6B', fontFamily: 'Manrope' }} data-testid="project-search-count">
            {loading ? 'Searching…' : `${count} result${count === 1 ? '' : 's'}`}
          </p>
          {loading && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin" style={{ color: '#003DA5' }} />
            </div>
          )}
          {!loading && results.length === 0 && (
            <div className="text-center py-12 text-sm" style={{ color: '#5C6B6B', fontFamily: 'Manrope' }}>
              No projects match your filters. Try clearing them.
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {results.map((r, idx) => (
              <ResultCard
                key={`${r.type}-${r.area_id}-${idx}`}
                r={r}
                testid={`project-result-${idx}`}
                onClick={() => { onPickArea(r.area_id); onClose(); }}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function FilterChip({ id, label, icon: Icon, color, active, onClick, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-bold transition"
      style={{
        backgroundColor: active ? (color || '#003DA5') : 'white',
        color: active ? 'white' : '#1A2E2E',
        border: `1px solid ${active ? (color || '#003DA5') : '#D6D0C0'}`,
        fontFamily: 'Manrope',
      }}
    >
      {Icon && <Icon className="w-3 h-3" />} {label}
    </button>
  );
}

function ResultCard({ r, testid, onClick }) {
  const statusColors = {
    completed:    { bg: '#DCFCE7', fg: '#15803D', icon: CheckCircle2 },
    'in-progress':{ bg: '#FEF3C7', fg: '#B45309', icon: Clock },
    planned:      { bg: '#DBEAFE', fg: '#1F4FBE', icon: Calendar },
  };
  const sc = statusColors[r.status] || statusColors.planned;
  const StatusIcon = sc.icon;
  const kindLabel = {
    'reconstruction': 'Reconstruction',
    'crucial-enhancement': 'Crucial Enhancement',
    'requested-enhancement': 'Requested Enhancement',
  }[r.kind] || r.kind;
  const kindAccent = {
    'reconstruction': '#0EA5E9',
    'crucial-enhancement': '#DC2626',
    'requested-enhancement': '#F59E0B',
  }[r.kind] || '#5C6B6B';

  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className="text-left rounded-2xl p-4 transition hover:-translate-y-0.5 hover:shadow-lg group"
      style={{
        backgroundColor: 'white',
        border: '1px solid #E2DBC8',
        boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
      }}
    >
      <div className="flex items-start justify-between gap-2 mb-1.5">
        <span
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider"
          style={{ backgroundColor: sc.bg, color: sc.fg, fontFamily: 'Manrope' }}
        >
          <StatusIcon className="w-2.5 h-2.5" /> {r.status.replace('-', ' ')}
        </span>
        <span
          className="text-[9px] font-bold uppercase tracking-wider"
          style={{ color: kindAccent, fontFamily: 'Manrope' }}
        >
          {kindLabel}
        </span>
      </div>
      <h3 className="text-sm font-bold mb-1 line-clamp-2" style={{ color: '#1A2E2E', fontFamily: 'Manrope' }}>
        {r.title}
      </h3>
      {r.subtitle && (
        <p className="text-[11px] mb-2" style={{ color: '#5C6B6B', fontFamily: 'Manrope' }}>{r.subtitle}</p>
      )}
      {r.description && (
        <p className="text-[11px] line-clamp-2 mb-2" style={{ color: '#7A8C8C', fontFamily: 'Manrope' }}>{r.description}</p>
      )}
      {r.progress_percent > 0 && (
        <div className="h-1.5 rounded-full overflow-hidden mb-1" style={{ backgroundColor: '#E5E7EB' }}>
          <div className="h-full rounded-full transition-all" style={{ width: `${r.progress_percent}%`, backgroundColor: kindAccent }} />
        </div>
      )}
      <div className="flex items-center justify-between text-[10px]" style={{ fontFamily: 'Manrope' }}>
        <span style={{ color: '#5C6B6B' }}>{r.progress_percent}% complete</span>
        <span className="flex items-center gap-1 font-bold opacity-0 group-hover:opacity-100 transition" style={{ color: '#003DA5' }}>
          Open <ArrowRight className="w-3 h-3" />
        </span>
      </div>
    </button>
  );
}

// Wrench/Hammer icon imports above are referenced via the chip icons; kept for completeness.
export { Wrench, Hammer };
